/**
 * Contratos de domínio do módulo GINGADO (psicomotricidade) — local-first, PT-BR.
 *
 * Espelha a gramática do módulo alimentar (src/types.ts) e do musical (src/music/
 * types.ts), traduzida para o domínio corporal: "alimento"/"experiência musical" →
 * "atividade psicomotora"; "passo de aproximação"/"passo de engajamento" → "passo de
 * participação". Reaproveita a entidade Criança e a tabela `foto` (Blob) do app.
 *
 * Decisões de arquitetura (vindas da spec docs/consulta/estudo-modulo-psicomotricidade.html):
 *  - a `escada de participação` é um enum ORDENADO desde já (o número É o passo);
 *  - `origem` do momento é gravada SEMPRE (higiene de dados);
 *  - o objeto `psicomotor` (7 fatores de Fonseca + sistema sensório-motor) é dado
 *    estruturado, não texto livre;
 *  - o `eixo de regulação tônica` (acalmar↔ativar) usa O MESMO enum do Compasso
 *    (importado abaixo) — é a base da timeline unificada de regulação (§14).
 *
 * Em caso de dúvida, o código é a fonte da verdade.
 */
import type { Origem, Tendencia } from "../types";
import { ORIGEM_LABEL } from "../types";
// Eixo de regulação tônica compartilhado com o Compasso (§7/§14 da spec). Importado
// e re-exportado para que body e music falem exatamente a mesma língua de regulação.
import {
  EFEITO_LABEL,
  ESTADO_REGULACAO_LABEL,
  type EfeitoRegulatorio,
  type EstadoRegulacao,
} from "../music/types";

export {
  EFEITO_LABEL,
  ESTADO_REGULACAO_LABEL,
  type EfeitoRegulatorio,
  type EstadoRegulacao,
};
export { type Origem, ORIGEM_LABEL };

// ──────────────────────────────────────────────────────────────────────────
// Escada de Participação Psicomotora (o coração do módulo) — enum ORDENADO
// ──────────────────────────────────────────────────────────────────────────

/**
 * Progressão relacional-motora da psicomotricidade relacional (Aucouturier) cruzada
 * com o gradiente de ajuda decrescente da psicomotricidade dirigida: tolerar o
 * movimento → orientar-se → explorar → experimentar → realizar com apoio → imitar →
 * realizar sozinho → jogo a dois → iniciativa.
 * O valor numérico É o passo. Compare com < e >. Subir de 0 → 3 já é vitória.
 */
export enum PassoParticipacao {
  PresencaCorporal = 0, // está no espaço da atividade e observa; ainda não participa
  Tolerancia = 1, // permanece próximo; aceita ser tocado/movido (diálogo tônico)
  Orientacao = 2, // acompanha com olhar e corpo o movimento/objeto/parceiro
  Exploracao = 3, // explora livremente o material e o próprio corpo no espaço
  ExperimentacaoDirigida = 4, // tenta a ação proposta, ainda que parcial / com muita ajuda
  RealizacaoComApoio = 5, // executa a ação com ajuda física ou verbal do parceiro
  Imitacao = 6, // reproduz gesto, postura ou sequência proposta (práxis/imitação)
  RealizacaoAutonoma = 7, // faz sozinho, sem ajuda
  JogoADois = 8, // alterna no jogo de movimento com o parceiro: eu → você → eu
  Iniciativa = 9, // propõe, lidera ou inventa uma sequência ou jogo motor próprio
}

/** Rótulos em sentence case para a UI. */
export const PARTICIPACAO_LABEL: Record<PassoParticipacao, string> = {
  [PassoParticipacao.PresencaCorporal]: "presença corporal",
  [PassoParticipacao.Tolerancia]: "tolerância",
  [PassoParticipacao.Orientacao]: "orientação",
  [PassoParticipacao.Exploracao]: "exploração",
  [PassoParticipacao.ExperimentacaoDirigida]: "experimentação dirigida",
  [PassoParticipacao.RealizacaoComApoio]: "realização com apoio",
  [PassoParticipacao.Imitacao]: "imitação",
  [PassoParticipacao.RealizacaoAutonoma]: "realização autônoma",
  [PassoParticipacao.JogoADois]: "jogo a dois",
  [PassoParticipacao.Iniciativa]: "iniciativa / criação",
};

/** Descrição curta de cada passo (ajuda o cuidador a marcar com confiança). */
export const PARTICIPACAO_DESC: Record<PassoParticipacao, string> = {
  [PassoParticipacao.PresencaCorporal]: "está no espaço da atividade e observa; ainda não participa",
  [PassoParticipacao.Tolerancia]: "permanece próximo; aceita ser tocado ou movido sem se retirar",
  [PassoParticipacao.Orientacao]: "acompanha com olhar e corpo o movimento, o objeto ou o parceiro",
  [PassoParticipacao.Exploracao]: "explora livremente o material e o próprio corpo no espaço",
  [PassoParticipacao.ExperimentacaoDirigida]: "tenta a ação proposta, ainda que parcial ou com muita ajuda",
  [PassoParticipacao.RealizacaoComApoio]: "executa a ação com ajuda física ou verbal do parceiro",
  [PassoParticipacao.Imitacao]: "reproduz gesto, postura ou sequência proposta",
  [PassoParticipacao.RealizacaoAutonoma]: "faz sozinho, sem ajuda",
  [PassoParticipacao.JogoADois]: "alterna no jogo de movimento: eu → você → eu",
  [PassoParticipacao.Iniciativa]: "propõe, lidera ou inventa uma sequência ou jogo próprio",
};

export const PARTICIPACAO_MIN = PassoParticipacao.PresencaCorporal; // 0
export const PARTICIPACAO_MAX = PassoParticipacao.Iniciativa; // 9

// ──────────────────────────────────────────────────────────────────────────
// Enums / unions da atividade
// ──────────────────────────────────────────────────────────────────────────

/** Natureza da atividade psicomotora. */
export type TipoAtividade =
  | "jogo"
  | "circuito"
  | "material"
  | "exercicio"
  | "brincadeira";

export const TIPO_LABEL: Record<TipoAtividade, string> = {
  jogo: "jogo",
  circuito: "circuito",
  material: "material",
  exercicio: "exercício",
  brincadeira: "brincadeira",
};

export const TIPOS_ORDEM: TipoAtividade[] = [
  "jogo",
  "circuito",
  "material",
  "exercicio",
  "brincadeira",
];

/** Com quem a atividade se faz. */
export type DemandaRelacional = "solo" | "parceiro" | "grupo";

export const DEMANDA_LABEL: Record<DemandaRelacional, string> = {
  solo: "sozinho",
  parceiro: "a dois",
  grupo: "em grupo",
};

export const DEMANDAS_ORDEM: DemandaRelacional[] = ["solo", "parceiro", "grupo"];

// ──────────────────────────────────────────────────────────────────────────
// Objeto `psicomotor` — dimensões estruturadas (7 fatores de Vítor da Fonseca +
// sistema sensório-motor). Base para "atividades parecidas" (P1) e para explicar
// POR QUE uma atividade funciona.
// ──────────────────────────────────────────────────────────────────────────

/** Os 7 fatores psicomotores da Bateria Psicomotora (Fonseca). */
export type FatorPsicomotor =
  | "tonicidade"
  | "equilibracao"
  | "lateralizacao"
  | "nocao_corpo"
  | "espaco_tempo"
  | "praxia_global"
  | "praxia_fina";

export const FATOR_LABEL: Record<FatorPsicomotor, string> = {
  tonicidade: "tonicidade",
  equilibracao: "equilibração",
  lateralizacao: "lateralização",
  nocao_corpo: "noção do corpo",
  espaco_tempo: "estruturação espaço-temporal",
  praxia_global: "praxia global",
  praxia_fina: "praxia fina",
};

export const FATORES_ORDEM: FatorPsicomotor[] = [
  "tonicidade",
  "equilibracao",
  "lateralizacao",
  "nocao_corpo",
  "espaco_tempo",
  "praxia_global",
  "praxia_fina",
];

/** Sistema(s) sensório-motor(es) trabalhado(s) — pode ser >1. */
export type SistemaSensorioMotor =
  | "vestibular"
  | "proprioceptivo"
  | "tatil"
  | "motor_global"
  | "motor_fino";

export const SISTEMA_LABEL: Record<SistemaSensorioMotor, string> = {
  vestibular: "vestibular",
  proprioceptivo: "proprioceptivo",
  tatil: "tátil",
  motor_global: "motor global",
  motor_fino: "motor fino",
};

export const SISTEMAS_ORDEM: SistemaSensorioMotor[] = [
  "vestibular",
  "proprioceptivo",
  "tatil",
  "motor_global",
  "motor_fino",
];

/** Intensidade tônica esperada da atividade (acalma ↔ ativa). */
export type IntensidadePsico = "calmante" | "neutra" | "ativadora";

export const INTENSIDADE_LABEL: Record<IntensidadePsico, string> = {
  calmante: "calmante",
  neutra: "neutra",
  ativadora: "ativadora",
};

/** Previsibilidade do estímulo (alta organiza; baixa surpreende). */
export type Previsibilidade = "alta" | "media" | "baixa";

export const PREVISIBILIDADE_LABEL: Record<Previsibilidade, string> = {
  alta: "alta",
  media: "média",
  baixa: "baixa",
};

/**
 * Perfil psicomotor — dimensões estruturadas. `fator_alvo` é o fator de Fonseca
 * principal; `sistema` são os sistemas sensório-motores envolvidos (pode ser >1).
 */
export interface PerfilPsicomotor {
  fator_alvo: FatorPsicomotor | null;
  sistema: SistemaSensorioMotor[];
  intensidade: IntensidadePsico | null;
  previsibilidade: Previsibilidade | null;
}

/** Status da atividade. NÃO é nota — é situação. Nunca renderize como erro/vermelho. */
export type StatusAtividade = "favorita" | "em_exploracao" | "sensivel" | "nao_usada";

export const STATUS_ATIVIDADE_LABEL: Record<StatusAtividade, string> = {
  favorita: "favorita",
  em_exploracao: "em exploração",
  sensivel: "sensível",
  nao_usada: "não usada",
};

// ──────────────────────────────────────────────────────────────────────────
// Gradiente de ajuda (ajuda decrescente = dado clínico de autonomia)
// ──────────────────────────────────────────────────────────────────────────

/** Nível de ajuda no momento — opcional. Menos ajuda no tempo = mais autonomia. */
export type NivelAjuda = "nenhuma" | "verbal" | "fisica_leve" | "fisica_plena";

export const AJUDA_LABEL: Record<NivelAjuda, string> = {
  nenhuma: "sem ajuda",
  verbal: "ajuda verbal",
  fisica_leve: "ajuda física leve",
  fisica_plena: "ajuda física plena",
};

export const AJUDAS_ORDEM: NivelAjuda[] = [
  "nenhuma",
  "verbal",
  "fisica_leve",
  "fisica_plena",
];

/** Autonomia DERIVADA: a ajuda vem diminuindo ao longo do tempo? */
export type Autonomia = "cresce" | "estavel" | "poucos_dados";

export const AUTONOMIA_LABEL: Record<Autonomia, string> = {
  cresce: "autonomia crescendo",
  estavel: "autonomia estável",
  poucos_dados: "poucos dados",
};

// ──────────────────────────────────────────────────────────────────────────
// Contexto do momento
// ──────────────────────────────────────────────────────────────────────────

/** Contexto do momento psicomotor (opcional). */
export type ContextoMomento =
  | "transicao"
  | "antes_tarefa"
  | "antes_refeicao"
  | "recreio"
  | "sono"
  | "outro";

export const CONTEXTO_LABEL: Record<ContextoMomento, string> = {
  transicao: "transição",
  antes_tarefa: "antes de uma tarefa",
  antes_refeicao: "antes da refeição",
  recreio: "recreio / brincadeira",
  sono: "antes do sono",
  outro: "outro",
};

export const CONTEXTOS_ORDEM: ContextoMomento[] = [
  "transicao",
  "antes_tarefa",
  "antes_refeicao",
  "recreio",
  "sono",
  "outro",
];

// ──────────────────────────────────────────────────────────────────────────
// Entidades
// ──────────────────────────────────────────────────────────────────────────

/** Atividade psicomotora (biblioteca) — rica, definida 1×. */
export interface AtividadePsicomotora {
  id: string;
  nome: string;
  tipo: TipoAtividade;
  midias: string[]; // refs locais (foto do material / clipe do jogo); a 1ª é a capa
  psicomotor: PerfilPsicomotor;
  demanda_relacional: DemandaRelacional | null;
  status: StatusAtividade;
  seguranca: string | null; // cuidados físicos (visível antes da atividade)
  observacoes: string | null;
  arquivado: boolean; // arquivar em vez de deletar
  criado_em: string; // ISO 8601
}

/**
 * Momento psicomotor (evento) — mínimo, recorrente, ≤10s. Só atividade_id,
 * crianca_id, data_hora, passo_participacao e origem são necessários para salvar.
 * Todo o resto é opcional e NUNCA bloqueia.
 */
export interface MomentoPsicomotor {
  id: string;
  atividade_id: string;
  crianca_id: string;
  data_hora: string; // ISO 8601
  passo_participacao: PassoParticipacao; // 0–9 (obrigatório — é a "reação")
  nivel_ajuda: NivelAjuda | null; // opcional — gradiente de ajuda
  estado_antes: EstadoRegulacao | null; // opcional — eixo de regulação (§7), mesmo enum do Compasso
  estado_depois: EstadoRegulacao | null; // opcional — eixo de regulação (§7)
  duracao_min: number | null; // opcional
  contexto: ContextoMomento | null; // opcional
  com_quem: string[]; // pode ser []
  origem: Origem; // gravado automaticamente (cuidador | crianca | profissional)
  confirmado: boolean; // origem "crianca" nasce pendente (false)
  notas: string | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Derivados por atividade (CALCULADOS, não persistidos)
// ──────────────────────────────────────────────────────────────────────────

export interface DerivadosAtividade {
  atividade_id: string;
  n_momentos: number;
  ultimo_momento: string | null; // ISO 8601
  passo_atual: PassoParticipacao | null; // passo do momento mais recente
  passo_maximo: PassoParticipacao | null; // maior passo de participação já atingido
  tendencia: Tendencia;
  efeito_regulatorio: EfeitoRegulatorio;
  autonomia: Autonomia; // ajuda decrescente ao longo do tempo
  dias_desde_ultimo: number | null; // alimenta o lembrete de rotina (P0.6)
}
